import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { ComponentView } from 'src/modules/app-template/models/component-view.model';
import { RestEndpoint } from 'src/modules/sm-base/models/rest-endpoint.model';
import { TableData } from 'src/modules/sm-base/models/table-data.model';
import { TableRow } from 'src/modules/sm-base/models/table-row.model';
import { FrontendField } from 'src/modules/sm-base/netgen/FrontendField';
import { FrontendFieldListItem } from 'src/modules/sm-base/netgen/FrontendFieldListItem';
import { FrontendFieldType } from 'src/modules/sm-base/netgen/FrontendFieldType';
import { FrontendForm } from 'src/modules/sm-base/netgen/FrontendForm';
import { TableCellType } from 'src/modules/sm-base/netgen/TableCellType';
import { TableColumn } from 'src/modules/sm-base/netgen/TableColumn';
import { Arrays } from 'src/modules/utils/shared/arrays';
import { Dates } from "src/modules/utils/shared/dates";
import { ObjectOfSimpleDataTypes } from 'src/modules/utils/shared/object-of-simple-data-types.model';
import { Objects } from "src/modules/utils/shared/objects";
import { EnaioCertificateService } from '../../services/enaio-certificate.service';
import { ClassRegisterSettings } from '../../shared/class-register-settings.entity';
import { EnaioCertificatesTools } from '../../shared/enaio-certificates-tools';
import { Holiday } from '../../shared/holiday.entity';
import { SchoolType } from '../../shared/school-type.enum';
import { SpecialDomain } from '../../shared/special-domain.entity';
import { TeacherAdditional } from '../../shared/teacher-additional.entity';
import { Teacher } from '../../shared/teacher.model';

@Component({
    selector: 'app-class-register-admin',
    templateUrl: './class-register-admin.component.html',
    styleUrls: ['./class-register-admin.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ClassRegisterAdminComponent extends ComponentView {

    service = inject(EnaioCertificateService);

    neededParams: ObjectOfSimpleDataTypes = { year: "number", isHalfYear: "bool" };
    year: number;
    isHalfYear: boolean;

    settings: ClassRegisterSettings;
    orgSettings: ClassRegisterSettings;
    teachers: Teacher[];

    form: FrontendForm;
    domains: string[];
    allowedDomainsTable: TableData;
    specialDomainsTable: TableData;
    teacherTable: TableData;
    holidayTable: TableData;
    bWeeksTable: TableData;

    async initParams(): Promise<boolean> {
        this.fullScreenComponent = false;
        [this.settings, this.teachers] = await Promise.all([
            this.service.restGetClassRegisterSettings(this.year, this.isHalfYear),
            this.service.restGetTeachers()
        ]);

        this.orgSettings = Objects.cloneDeep(this.settings);

        this.domains = await this.service.restGetDomains(null, null, true);

        this.app.updateNavigation("Einstellungen", [
            this.service.getHomeClassRegister(),
            { label: "Einstellungen", routerLink: ["/enaio-certificates", "class-registers", "admin", this.year, this.isHalfYear] }
        ]);

        this.form = new FrontendForm([
            new FrontendField("firstDayOfSchoolYear", "Schuljahresbeginn", FrontendFieldType.date, { value: this.settings.firstDayOfSchoolYear}),
            new FrontendField("lastDayOfSchoolYear", "Schuljahresende", FrontendFieldType.date, { value: this.settings.lastDayOfSchoolYear}),
            new FrontendField("managerElementary", "Bereichsleiter " + EnaioCertificatesTools.getSchoolTypeNames()[SchoolType.elementary], FrontendFieldType.dropdown, {
                value: this.settings.managerElementary,
                listItems: FrontendFieldListItem.make(this.teachers.filter(t => t.isFor(SchoolType.elementary, this.settings)), item => [item.userName, item.getDisplayName()], true, [["", ""]])
            }),
            new FrontendField("managerElementary2", "Bereichsleiter " + EnaioCertificatesTools.getSchoolTypeNames()[SchoolType.elementary], FrontendFieldType.dropdown, {
                value: this.settings.managerElementary2,
                hasLabel: false,
                listItems: FrontendFieldListItem.make(this.teachers.filter(t => t.isFor(SchoolType.elementary, this.settings)), item => [item.userName, item.getDisplayName()], true, [["", ""]])
            }),
            new FrontendField("managerSecondary", "Bereichsleiter " + EnaioCertificatesTools.getSchoolTypeNames()[SchoolType.secondary], FrontendFieldType.dropdown, {
                value: this.settings.managerSecondary,
                listItems: FrontendFieldListItem.make(this.teachers.filter(t => t.isFor(SchoolType.secondary, this.settings)), item => [item.userName, item.getDisplayName()], true, [["", ""]])
            }),
            new FrontendField("managerSecondary2", "Bereichsleiter " + EnaioCertificatesTools.getSchoolTypeNames()[SchoolType.secondary], FrontendFieldType.dropdown, {
                value: this.settings.managerSecondary2,
                hasLabel: false,
                listItems: FrontendFieldListItem.make(this.teachers.filter(t => t.isFor(SchoolType.secondary, this.settings)), item => [item.userName, item.getDisplayName()], true, [["", ""]])
            }),
            new FrontendField("managerSpecial", "Bereichsleiter " + EnaioCertificatesTools.getSchoolTypeNames()[SchoolType.special], FrontendFieldType.dropdown, {
                value: this.settings.managerSpecial,
                listItems: FrontendFieldListItem.make(this.teachers.filter(t => t.isFor(SchoolType.special, this.settings)), item => [item.userName, item.getDisplayName()], true, [["", ""]])
            }),
            new FrontendField("managerSpecial2", "Bereichsleiter " + EnaioCertificatesTools.getSchoolTypeNames()[SchoolType.special], FrontendFieldType.dropdown, {
                value: this.settings.managerSpecial2,
                hasLabel: false,
                listItems: FrontendFieldListItem.make(this.teachers.filter(t => t.isFor(SchoolType.special, this.settings)), item => [item.userName, item.getDisplayName()], true, [["", ""]])
            }),
            new FrontendField("managerBusinessUnit", "Geschäftsbereichsleiter", FrontendFieldType.dropdown, {
                value: this.settings.managerBusinessUnit,
                listItems: FrontendFieldListItem.make(this.teachers, item => [item.userName, item.getDisplayName()], true, [["", ""]])
            }),
            new FrontendField("gradeLevelCoordinator", "Stufenkoordinator", FrontendFieldType.dropdown, {
                value: this.settings.gradeLevelCoordinator,
                listItems: FrontendFieldListItem.make(this.teachers, item => [item.userName, item.getDisplayName()], true, [["", ""]])
            }),
            new FrontendField("gradeLevelCoordinator2", "Stufenkoordinator", FrontendFieldType.dropdown, {
                value: this.settings.gradeLevelCoordinator2,
                hasLabel: false,
                listItems: FrontendFieldListItem.make(this.teachers, item => [item.userName, item.getDisplayName()], true, [["", ""]])
            }),
            new FrontendField("updatePupilCourses", "enaio Arbeitsgemeinschaften aktualisieren", FrontendFieldType.button, { noMapping: true, onClick: this.updatePupilCourses.bind(this)})
        ]);

        this.allowedDomainsTable = new TableData([
            new TableColumn("domain", "Fach", TableCellType.string, 30),
            new TableColumn("allowed", "Erlaubt", TableCellType.bool, 1, {
                editable: true
            })
        ], this.domains.map(domain => new TableRow("", domain, {
            domain,
            allowed: this.settings.allowedDomains.includes(domain)
        })));

        this.specialDomainsTable = new TableData([
            new TableColumn("title", "Bezeichnung", TableCellType.string, 30, {
                editable: true
            }),
            new TableColumn("isPlaceholder", "Ist Platzhalter", TableCellType.bool, 1, {
                editable: true
            })
        ], this.settings.specialDomains.map(sd => new TableRow("", sd)), ["title"], {
            generator: async () => Promise.resolve(new TableRow("", new SpecialDomain()))
        });

        this.teacherTable = new TableData([
            new TableColumn("name", "Name", TableCellType.string, 30),
            new TableColumn("forElementary", "Grundschule", TableCellType.bool, 1, {
                editable: true
            }),
            new TableColumn("forSecondary", "Gesamtschule", TableCellType.bool, 1, {
                editable: true
            }),
            new TableColumn("forSpecial", "Förderschule", TableCellType.bool, 1, {
                editable: true
            })
        ], this.teachers.map(t => new TableRow("", t, {
            name: t.fullName,
            forElementary: Arrays.findAndConvert(this.settings.teacher, t2 => t2.userName == t.userName, t2 => t2.forElementary, false),
            forSecondary: Arrays.findAndConvert(this.settings.teacher, t2 => t2.userName == t.userName, t2 => t2.forSecondary, false),
            forSpecial: Arrays.findAndConvert(this.settings.teacher, t2 => t2.userName == t.userName, t2 => t2.forSpecial, false)
        })), ["name"]);

        this.holidayTable = new TableData([
            new TableColumn("from", "Von", TableCellType.date, 1, {
                editable: true,
                sortable: false
            }),
            new TableColumn("to", "Bis", TableCellType.date, 1, {
                editable: true,
                sortable: false
            }),
            new TableColumn("title", "Bezeichnung", TableCellType.string, 40, {
                editable: true,
                sortable: false
            })
        ], this.settings.holidays.map(h => new TableRow("", h)), ["from"], {
            generator: async () => Promise.resolve(new TableRow("", Objects.fromPlain(Holiday, {
                from: new Date(),
                to: new Date(),
                title: ""
            })))
        });

        this.bWeeksTable = new TableData([
            new TableColumn("calendarWeek", "KW", TableCellType.string, 5),
            new TableColumn("date", "Datum", TableCellType.date, 1),
            new TableColumn("bWeek", "B-Woche", TableCellType.bool, 1, {
                editable: true
            })
        ], Arrays.mapWithIndex(this.settings.getWeekDates(), (date, index) => new TableRow("", date, {
            calendarWeek: Dates.calendarWeek(date),
            date,
            bWeek: Arrays.getSafe(this.settings.bWeeks, index, false)
        })));
        return true;
    }

    override getCanDeactivateMessage(): string {
        return this.isChanged() ? "" : null;
    }

    async updatePupilCourses(): Promise<void> {
        await RestEndpoint.main().post().query({ year: this.year, isHalfYear: this.isHalfYear }).run("api/cer/pupil/updatePupilCourses").getText();
    }

    isChanged(): boolean {
        let gui = this.fromGui();
        this.orgSettings.specialDomains.sort((a, b) => Objects.cmp(a.id, b.id));
        gui.specialDomains.sort((a, b) => Objects.cmp(a.id, b.id));
        return this.form != null && this.settings != null && !Objects.equalsDeep(gui, this.orgSettings);
    }

    addSpecialDomain(): void {
        this.specialDomainsTable.rows = [...this.specialDomainsTable.rows, new TableRow("", new SpecialDomain())];
    }

    addHoliday(): void {
        this.holidayTable.rows = [...this.holidayTable.rows, new TableRow("", Objects.fromPlain(Holiday, {
            from: new Date(),
            to: new Date(),
            title: ""
        }))];
    }

    private fromGui(): ClassRegisterSettings {
        let result = Objects.cloneDeep(this.settings);
        this.form.get(this.settings);
        result.dutyNames = result.dutyNames.filter(d => d != "");
        result.instructionTexts = result.instructionTexts.filter(d => d != "");
        result.specialDomains = this.specialDomainsTable.rows.map(sd => sd.raw as SpecialDomain);
        result.holidays = this.holidayTable.rows.map(h => h.raw as Holiday);
        result.bWeeks = this.bWeeksTable.rows.map(r => r.values.bWeek as boolean);
        result.allowedDomains = Arrays.sort(this.allowedDomainsTable.rows.filter(r => r.values.allowed as boolean).map(r => r.values.domain as string));

        for (let t of this.teacherTable.rows) {
            let teacher = result.teacher.find(t2 => t2.userName == t.raw.userName);
            if (teacher == null && (t.values.forElementary || t.values.forSecondary || t.values.forSpecial)) {
                teacher = Objects.fromPlain(TeacherAdditional, {
                    userName: t.raw.userName
                });
                result.teacher.push(teacher);
            }
            if (teacher != null) {
                teacher.forElementary = t.values.forElementary ?? false;
                teacher.forSecondary = t.values.forSecondary ?? false;
                teacher.forSpecial = t.values.forSpecial ?? false;
            }
        }
        return result;
    }

    async save(): Promise<void> {
        await this.app.saveDataHandler(async () => RestEndpoint.main().body(this.fromGui()).post().run("api/cer/classregister/settings").getText(), this, true, true);
    }
}
